/**
 * GraphVisualization component - Interactive network topology visualization using Cytoscape.js.
 *
 * This component renders network graphs with switches, VLANs, and interfaces,
 * supporting interactive features like zoom, pan, drag, and hover tooltips.
 */

import { forwardRef, useEffect, useRef, useState } from 'react';
import cytoscape, { Core } from 'cytoscape';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import type { GraphData, GraphNode, GraphEdge } from '@/types/entities';

export interface GraphVisualizationProps {
  /** Graph data containing nodes and edges */
  data: GraphData;
  /** Height of the graph container (default: '600px') */
  height?: string | number;
  /** Layout algorithm to use (default: 'cose') */
  layout?: 'cose' | 'circle' | 'grid' | 'breadthfirst';
  /** Callback fired when a node is clicked */
  onNodeClick?: (node: GraphNode) => void;
  /** Callback fired when an edge is clicked */
  onEdgeClick?: (edge: GraphEdge) => void;
  /** ID of node to center and highlight */
  centerNodeId?: string;
  /** Show loading state */
  loading?: boolean;
  /** Custom Cytoscape styles to override defaults */
  customStyles?: cytoscape.StylesheetStyle[];
}

const MAX_NODES_WARNING = 500;

export const GraphVisualization = forwardRef<HTMLDivElement, GraphVisualizationProps>(
  (
    {
      data,
      height = '600px',
      layout = 'cose',
      onNodeClick,
      onEdgeClick,
      centerNodeId,
      loading = false,
      customStyles,
    },
    forwardedRef
  ) => {
    const internalRef = useRef<HTMLDivElement>(null);
    const containerRef = (forwardedRef as React.RefObject<HTMLDivElement>) || internalRef;
  const cyRef = useRef<Core | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showWarning, setShowWarning] = useState(false);

  // Initialize Cytoscape instance
  useEffect(() => {
    if (!containerRef.current || loading || data.nodes.length === 0) {
      return;
    }

    // Restore pointer events in case they were disabled during cleanup
    if (containerRef.current) {
      (containerRef.current as HTMLElement).style.pointerEvents = 'auto';
    }

    try {
      // Check for too many nodes
      if (data.nodes.length > MAX_NODES_WARNING) {
        setShowWarning(true);
      } else {
        setShowWarning(false);
      }

      // Transform data to Cytoscape format
      const elements = {
        nodes: data.nodes.map((node) => ({
          data: {
            id: node.id,
            label: node.label,
            type: node.type,
            nodeData: node.data,
          },
        })),
        edges: data.edges.map((edge) => ({
          data: {
            id: edge.id,
            source: edge.source,
            target: edge.target,
            type: edge.type,
            label: edge.label,
            edgeData: edge.data,
          },
        })),
      };

      // Initialize Cytoscape
      const cy = cytoscape({
        container: containerRef.current,
        elements,
        layout: getLayoutConfig(layout),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        style: (customStyles || getCytoscapeStyles()) as any, // Type workaround for Cytoscape style definitions
        userZoomingEnabled: true,
        userPanningEnabled: true,
        boxSelectionEnabled: false,
        minZoom: 0.3,
        maxZoom: 3,
      });

      cyRef.current = cy;

      // Store instance on container element for GraphControls access
      if (containerRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (containerRef.current as any).cytoscapeInstance = cy;
      }

      // Add event listeners
      cy.on('tap', 'node', (event) => {
        const node = event.target;
        const nodeData: GraphNode = {
          id: node.data('id'),
          label: node.data('label'),
          type: node.data('type'),
          data: node.data('nodeData'),
        };
        if (onNodeClick) {
          onNodeClick(nodeData);
        }
      });

      cy.on('tap', 'edge', (event) => {
        const edge = event.target;
        const edgeData: GraphEdge = {
          id: edge.data('id'),
          source: edge.data('source'),
          target: edge.data('target'),
          type: edge.data('type'),
          label: edge.data('label'),
          data: edge.data('edgeData'),
        };
        if (onEdgeClick) {
          onEdgeClick(edgeData);
        }
      });

      // Hover effects - highlight connected nodes and edges
      cy.on('mouseover', 'node', (event) => {
        const node = event.target;
        const connectedEdges = node.connectedEdges();
        const connectedNodes = connectedEdges.connectedNodes();

        // Dim everything first
        cy.nodes().style('opacity', '0.3');
        cy.edges().style('opacity', '0.3');

        // Then highlight the hovered node and its connections
        node.style('opacity', '1');
        connectedNodes.style('opacity', '1');
        connectedEdges.style('opacity', '1');
      });

      cy.on('mouseout', 'node', () => {
        // Restore opacity
        cy.nodes().style('opacity', '1');
        cy.edges().style('opacity', '1');
      });

      // Center on specific node if provided
      if (centerNodeId) {
        const centerNode = cy.getElementById(centerNodeId);
        if (centerNode.length > 0) {
          cy.animate({
            center: { eles: centerNode },
            zoom: 1.5,
          }, {
            duration: 500,
          });
          centerNode.addClass('highlighted');
        }
      }

      // Only clear error if it's currently set (avoid unnecessary re-render)
      if (error !== null) {
        setError(null);
      }
    } catch (err) {
      console.error('Failed to initialize Cytoscape:', err);
      setError('Failed to render graph visualization');
    }

    // Cleanup
    return () => {
      if (cyRef.current) {
        try {
          // Disable pointer events FIRST to prevent mouse handlers from firing during cleanup
          if (containerRef.current) {
            (containerRef.current as HTMLElement).style.pointerEvents = 'none';
          }

          // Stop all animations: clearQueue=true, jumpToEnd=true
          cyRef.current.stop(true, true);
          // Remove all event listeners to prevent dangling handlers
          cyRef.current.removeAllListeners();
          // Now safe to destroy
          cyRef.current.destroy();
        } catch (e) {
          // Ignore errors during cleanup
          console.warn('Error during Cytoscape cleanup:', e);
        } finally {
          // Clear instance from container element
          if (containerRef.current) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            (containerRef.current as any).cytoscapeInstance = null;
          }
          cyRef.current = null;
        }
      }
    };
  }, [data, layout, onNodeClick, onEdgeClick, centerNodeId, loading]);

  // Expose Cytoscape instance to parent via ref (for controls)
  useEffect(() => {
    if (cyRef.current && containerRef.current) {
      // Store cy instance on container for controls to access
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (containerRef.current as any).cytoscapeInstance = cyRef.current;
    }

    // Cleanup: remove reference when unmounting
    return () => {
      if (containerRef.current) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (containerRef.current as any).cytoscapeInstance = null;
      }
    };
  }, []); // Run once on mount to avoid cascading re-renders

  // Loading state
  if (loading) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: 1,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Empty state
  if (data.nodes.length === 0) {
    return (
      <Box
        sx={{
          height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: 1,
          backgroundColor: 'background.default',
        }}
      >
        <Typography variant="body1" color="text.secondary">
          No graph data to display
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      {showWarning && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This graph contains {data.nodes.length} nodes. Rendering may be slow.
          Consider filtering to improve performance.
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Box
        ref={containerRef}
        sx={{
          height,
          minHeight: '400px',
          border: '1px solid rgba(0, 0, 0, 0.12)',
          borderRadius: 1,
          boxShadow: 1,
          backgroundColor: '#fafafa',
        }}
      />
    </Box>
  );
});

GraphVisualization.displayName = 'GraphVisualization';

/**
 * Get Cytoscape layout configuration
 */
function getLayoutConfig(layoutType: 'cose' | 'circle' | 'grid' | 'breadthfirst') {
  const commonConfig = {
    fit: true,
    padding: 30,
    animate: true,
    animationDuration: 500,
  };

  switch (layoutType) {
    case 'cose':
      return {
        name: 'cose',
        ...commonConfig,
        idealEdgeLength: 100,
        nodeOverlap: 20,
        refresh: 20,
        randomize: false,
        componentSpacing: 100,
        nodeRepulsion: 400000,
        edgeElasticity: 100,
        nestingFactor: 5,
        gravity: 80,
        numIter: 1000,
        initialTemp: 200,
        coolingFactor: 0.95,
        minTemp: 1.0,
      };
    case 'circle':
      return {
        name: 'circle',
        ...commonConfig,
      };
    case 'grid':
      return {
        name: 'grid',
        ...commonConfig,
        rows: undefined as number | undefined,
        cols: undefined as number | undefined,
      };
    case 'breadthfirst':
      return {
        name: 'breadthfirst',
        ...commonConfig,
        directed: true,
        spacingFactor: 1.5,
      };
    default:
      return { name: 'cose', ...commonConfig };
  }
}

/**
 * Get Cytoscape style definitions
 */
function getCytoscapeStyles() {
  return [
    // Default node style
    {
      selector: 'node',
      style: {
        'background-color': '#666',
        'label': 'data(label)',
        'text-valign': 'bottom',
        'text-halign': 'center',
        'text-margin-y': 5,
        'font-size': '12px',
        'color': '#333',
        'text-outline-color': '#fff',
        'text-outline-width': 2,
        'overlay-opacity': 0,
      },
    },
    // Switch nodes
    {
      selector: 'node[type="switch"]',
      style: {
        'shape': 'ellipse',
        'background-color': '#1976d2',
        'width': 60,
        'height': 60,
        'font-size': '14px',
        'font-weight': 'bold',
      },
    },
    // VLAN nodes
    {
      selector: 'node[type="vlan"]',
      style: {
        'shape': 'roundrectangle',
        'background-color': '#ed6c02',
        'width': 50,
        'height': 50,
        'font-size': '12px',
      },
    },
    // Interface nodes - access mode (green)
    {
      selector: 'node[type="interface"]',
      style: {
        'shape': 'ellipse',
        'background-color': '#2e7d32',
        'width': 35,
        'height': 35,
        'font-size': '10px',
      },
    },
    // LAG nodes (Phase 2)
    {
      selector: 'node[type="lag"]',
      style: {
        'shape': 'diamond',
        'background-color': '#9c27b0',
        'width': 45,
        'height': 45,
        'font-size': '11px',
      },
    },
    // Highlighted node (centered)
    {
      selector: 'node.highlighted',
      style: {
        'border-width': 4,
        'border-color': '#f50057',
        'border-opacity': 1,
      },
    },
    // Hovered node
    {
      selector: 'node:active',
      style: {
        'overlay-opacity': 0.2,
        'overlay-color': '#000',
      },
    },
    // Default edge style
    {
      selector: 'edge',
      style: {
        'width': 2,
        'line-color': '#757575',
        'target-arrow-color': '#757575',
        'target-arrow-shape': 'triangle',
        'curve-style': 'bezier',
        'label': 'data(label)',
        'font-size': '9px',
        'text-background-color': '#fff',
        'text-background-opacity': 0.8,
        'text-background-padding': '2px',
      },
    },
    // HAS_INTERFACE relationship
    {
      selector: 'edge[type="HAS_INTERFACE"]',
      style: {
        'line-color': '#757575',
        'target-arrow-color': '#757575',
        'width': 2,
      },
    },
    // MEMBER_OF relationship (access port)
    {
      selector: 'edge[type="MEMBER_OF"]',
      style: {
        'line-color': '#2e7d32',
        'target-arrow-color': '#2e7d32',
        'width': 3,
      },
    },
    // TRUNKS_VLAN relationship (trunk port)
    {
      selector: 'edge[type="TRUNKS_VLAN"]',
      style: {
        'line-color': '#0288d1',
        'target-arrow-color': '#0288d1',
        'width': 2,
        'line-style': 'dashed',
      },
    },
    // AGGREGATES relationship (LAG)
    {
      selector: 'edge[type="AGGREGATES"]',
      style: {
        'line-color': '#9c27b0',
        'target-arrow-color': '#9c27b0',
        'width': 2,
        'line-style': 'dotted',
      },
    },
    // PROVIDES_L3_FOR relationship (SVI)
    {
      selector: 'edge[type="PROVIDES_L3_FOR"]',
      style: {
        'line-color': '#d32f2f',
        'target-arrow-color': '#d32f2f',
        'width': 2,
        'line-style': 'dashed',
      },
    },
  ];
}
